import { verifyPasswordResetCode, confirmPasswordReset, getAuth } from 'firebase/auth';
import { Typography, Button, ControlledPassword, useForm } from '@idu-nightlife/ui-kit';
import { Box, Container, Alert, Stack, AlertProps } from '@mui/material';
import { zodPasswordSchema } from '@idu-nightlife/nightlife-utils';
import IDuLogo from '../../../../assets/idu-logo-light.svg?react';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import React, { useState } from 'react';
import { z } from 'zod';

enum verifyResetCodeErrorCode {
  INVALID_OOB_CODE = 'auth/invalid-action-code',
  EXPIRED_OOB_CODE = 'auth/expired-action-code',
  USER_NOT_FOUND = 'auth/user-not-found',
  USER_DISABLED = 'auth/user-disabled',
}

enum confirmPasswordErrorCode {
  WEAK_PASSWORD = 'auth/weak-password',
}

// Validation schema
const resetPasswordConfirmSchema = z.object({
  newPassword: zodPasswordSchema,
});

type ResetPasswordConfirmFormData = z.infer<typeof resetPasswordConfirmSchema>;

const confirmPasswordResetMutation = () => ({
  mutationFn: async (body: { oobCode: string; newPassword: string }) => {
    const auth = getAuth();

    const email = await verifyPasswordResetCode(auth, body.oobCode);

    await confirmPasswordReset(auth, body.oobCode, body.newPassword);

    return { message: 'Password reset successfully', email };
  },
});

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<{
    title: string;
    body: string;
    severity: AlertProps['severity'];
  } | null>(null);
  const [success, setSuccess] = useState(false);

  const oobCode = searchParams.get('oobCode');
  const email = searchParams.get('email');

  const { mutateAsync, isPending } = useMutation(confirmPasswordResetMutation());

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordConfirmFormData>({
    resolver: zodResolver(resetPasswordConfirmSchema),
    defaultValues: {
      newPassword: '',
    },
  });

  const onSubmit = async (data: ResetPasswordConfirmFormData) => {
    if (!oobCode) return;

    try {
      setError(null);
      setSuccess(false);

      await mutateAsync({
        oobCode,
        newPassword: data.newPassword,
      });

      setSuccess(true);
    } catch (err: any) {
      console.error('Reset password error:', err);

      // Handle Firebase errors
      if (
        err.code === verifyResetCodeErrorCode.INVALID_OOB_CODE ||
        err.code === verifyResetCodeErrorCode.EXPIRED_OOB_CODE
      ) {
        setError({
          severity: 'error',
          title: 'Link has expired',
          body: 'Your request to reset your password has expired or has already been used. Try resetting your password again.',
        });
        return;
      }

      if (err.code === verifyResetCodeErrorCode.USER_DISABLED) {
        setError({
          severity: 'error',
          title: 'User is disabled',
          body: 'This user account has been disabled. Please contact support.',
        });
        return;
      }

      // Not a security problem, because they can look at the network request to see the response
      if (err.code === verifyResetCodeErrorCode.USER_NOT_FOUND) {
        setError({
          severity: 'error',
          title: 'User not found',
          body: 'No user found for this reset link. Please request a new password reset.',
        });
        return;
      }

      if (err.code === confirmPasswordErrorCode.WEAK_PASSWORD) {
        setError({
          severity: 'info',
          title: 'Password is too weak',
          body: 'Password is too weak. Please use a stronger password.',
        });
        return;
      }

      setError(err.message || 'Failed to reset password. The link may have expired.');
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography.Subheading>Password Reset Successfully!</Typography.Subheading>
            <Typography.BodyEmphasised>
              Password changed. You can now sign in with your new password.
            </Typography.BodyEmphasised>
          </Alert>
          <Button to="/login">Go to Sign In</Button>
        </Box>
      </Container>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <Stack
                spacing={2}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
                direction="column"
              >
                <Box sx={{ width: '100%' }}>
                  <IDuLogo
                    style={{ width: 160, display: 'block', margin: '0 auto', height: 100 }}
                  />
                </Box>

                <Typography.Subtitle>
                  Reset your Password for <strong>{email}</strong>
                </Typography.Subtitle>

                {error && (
                  <Alert severity={error.severity}>
                    <Typography.Body>{error.title}</Typography.Body>
                    <Typography.Subtitle>{error.body}</Typography.Subtitle>
                  </Alert>
                )}

                <ControlledPassword
                  name="newPassword"
                  control={control}
                  label="New Password"
                  fullWidth
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                />

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={isSubmitting || isPending}
                  loading={isSubmitting || isPending}
                >
                  {isSubmitting || isPending ? 'Continue' : 'Save'}
                </Button>

                {success && <Button to="/login">Continue</Button>}

                <Button to="/login" startIcon={<ArrowBackIcon />}>
                  Back to Sign In
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Container>
    </form>
  );
};
